import { Editor, type EditorEvents, type JSONContent } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { use, useCallback, useMemo } from "react"

import { EditorContext } from "../context"
import { isExtensionInManager, isMarkInSchema, isNodeInSchema } from "../utils"

export const useEditorContent = (content: string | null): JSONContent => {
	return useMemo(() => (content ? JSON.parse(content) : undefined), [content])
}

export const useEditorBlur = (initialContent: JSONContent, onBlur: (content: JSONContent) => void) => {
	return useCallback(
		({ editor }: EditorEvents["blur"]) => {
			if (JSON.stringify(initialContent) === JSON.stringify(editor.getJSON())) return
			onBlur(editor.getJSON())
		},
		[initialContent, onBlur],
	)
}

export const useFallbackEditor = (editor: Editor | undefined) => {
	const fallbackEditor = use(EditorContext)
	return editor !== undefined ? editor : fallbackEditor
}

export const useNodeSchemaWarning = (nodeName: string, editor: Editor) => {
	const inSchema = isNodeInSchema(nodeName, editor)
	if (!inSchema) console.warn(`Mark ${nodeName} not in the editor schema.`)
}

export const useMarkSchemaWarning = (markName: string, editor: Editor) => {
	const inSchema = isMarkInSchema(markName, editor)
	if (!inSchema) console.warn(`Mark ${markName} not in the editor schema.`)
}

export const useExtensionManagerWarning = (extensionName: string, editor: Editor) => {
	const inSchema = isExtensionInManager(extensionName, editor)
	if (!inSchema) console.warn(`Extension ${extensionName} not in the editor extension manager.`)
}

export const useEditorFocused = (editor: Editor) => {
	return useEditorState({
		editor,
		selector: () => editor.isFocused,
	})
}

export const useEditorEditable = (editor: Editor) => {
	return useEditorState({
		editor,
		selector: () => editor.isEditable,
	})
}
